/**
 * Auto-generated TypeScript types for Claude Agent SDK streaming.
 *
 * DO NOT EDIT MANUALLY
 * Generated from: claude_agent_sdk.types + agent-runtime/event_types.py
 * Run: pnpm run gen:types
 *
 * SDK Reference: https://github.com/anthropics/claude-agent-sdk-python/blob/main/src/claude_agent_sdk/types.py
 */

// ============================================
// Event Type Constants (for pattern matching)
// ============================================

/** Event types for Claude Agent SDK streaming */
export enum EventType {
	AwaitingApproval = "awaiting_approval",
	Cancelled = "cancelled",
	CompactBoundary = "compact_boundary",
	ContentBlockDelta = "content_block_delta",
	ContentBlockStart = "content_block_start",
	ContentBlockStop = "content_block_stop",
	Done = "done",
	Error = "error",
	KeepAlive = "keep-alive",
	McpAuthFailed = "mcp_auth_failed",
	McpError = "mcp_error",
	MessageComplete = "message_complete",
	MessageDelta = "message_delta",
	MessageSnapshot = "message_snapshot",
	MessageStart = "message_start",
	MessageStop = "message_stop",
	Metadata = "metadata",
	PermissionRequest = "permission_request",
	PlanDelta = "plan_delta",
	Reasoning = "reasoning",
	RunnerDiag = "RUNNER_DIAG",
	RunMetrics = "run_metrics",
	SandboxConnected = "sandbox-connected",
	SandboxConnecting = "sandbox-connecting",
	SandboxMetrics = "sandbox_metrics",
	SdkContentSnapshot = "sdk_content_snapshot",
	SdkError = "sdk_error",
	SdkInit = "sdk_init",
	SdkSuccess = "sdk_success",
	Stderr = "stderr",
	SystemMessage = "system_message",
	Thinking = "thinking",
	ToolResult = "tool_result",
	ToolUse = "tool_use",
	UserMessage = "user_message",
}

/** Content block types for message content classification */
export enum ContentBlockType {
	Plan = "plan",
	Reasoning = "reasoning",
	Text = "text",
	Thinking = "thinking",
	ToolResult = "tool_result",
	ToolUse = "tool_use",
}

// ============================================
// Claude Agent SDK Types
// ============================================

/** Text content block from SDK */
export interface TextBlock {
	text: string;
}

/** Thinking content block from SDK */
export interface ThinkingBlock {
	thinking: string;
	signature: string;
}

/** Tool use block from SDK */
export interface ToolUseBlock {
	id: string;
	name: string;
	input: Record<string, unknown>;
}

/** Tool result block from SDK */
export interface ToolResultBlock {
	tool_use_id: string;
	content?: unknown;
	is_error?: unknown;
}

/** Content block union type from SDK */
export type SdkContentBlock = TextBlock | ThinkingBlock | ToolUseBlock | ToolResultBlock;

/** User message from SDK */
export interface UserMessage {
	content: unknown;
	uuid?: unknown;
	parent_tool_use_id?: unknown;
	tool_use_result?: unknown;
}

/** Assistant message from SDK */
export interface AssistantMessage {
	content: unknown[];
	model: string;
	parent_tool_use_id?: unknown;
	error?: null | unknown;
}

/** Stream event from SDK */
export interface StreamEvent {
	uuid: string;
	session_id: string;
	event: Record<string, unknown>;
	parent_tool_use_id?: unknown;
}

/** Result message from SDK */
export interface ResultMessage {
	subtype: string;
	duration_ms: number;
	duration_api_ms: number;
	is_error: boolean;
	num_turns: number;
	session_id: string;
	total_cost_usd?: unknown;
	usage?: unknown;
	result?: unknown;
	structured_output?: unknown;
}

/** Message union type from SDK */
export type SdkMessage = UserMessage | AssistantMessage | StreamEvent | ResultMessage;

// ============================================
// SDK Error Types
// ============================================

/** Base SDK error */
export interface ClaudeSDKError {
	name: string;
	message: string;
}

/** CLI process error with structured data */
export interface ProcessError extends ClaudeSDKError {
	exit_code?: number;
	stderr?: string;
}

/** Claude Code not found error */
export interface CLINotFoundError extends ClaudeSDKError {}

/** CLI connection error */
export interface CLIConnectionError extends ClaudeSDKError {}

/** JSON decode error */
export interface CLIJSONDecodeError extends ClaudeSDKError {}

/** Union type of all SDK errors */
export type SdkError = ProcessError | CLINotFoundError | CLIConnectionError | CLIJSONDecodeError;

// ============================================
// Streaming Event Interfaces
// ============================================

/** Base streaming event */
export interface BaseStreamEvent {
	type: string;
	[key: string]: unknown;
}

/** Message start */
export interface MessageStartEvent extends BaseStreamEvent {
	type: EventType.MessageStart;
	message?: {
		id?: string;
		role?: string;
		content?: unknown[];
		usage?: { input_tokens?: number; output_tokens?: number };
	};
	[key: string]: unknown;
}

/** Message delta */
export interface MessageDeltaEvent extends BaseStreamEvent {
	type: EventType.MessageDelta;
	delta?: {
		text?: string;
		usage?: { output_tokens?: number };
		[key: string]: unknown;
	};
	[key: string]: unknown;
}

/** Message stop */
export interface MessageStopEvent extends BaseStreamEvent {
	type: EventType.MessageStop;
	message_id?: string;
	[key: string]: unknown;
}

/** Message complete */
export interface MessageCompleteEvent extends BaseStreamEvent {
	type: EventType.MessageComplete;
	usage?: { input_tokens?: number; output_tokens?: number };
	[key: string]: unknown;
}

/** Content block start */
export interface ContentBlockStartEvent extends BaseStreamEvent {
	type: EventType.ContentBlockStart;
	id?: string;
	block_type?: string;
	index?: number;
	content_block?: { type?: string; [key: string]: unknown };
	[key: string]: unknown;
}

/** Content block delta */
export interface ContentBlockDeltaEvent extends BaseStreamEvent {
	type: EventType.ContentBlockDelta;
	id?: string;
	delta?: {
		type?: string;
		text?: string;
		partial_json?: string;
		[key: string]: unknown;
	};
	[key: string]: unknown;
}

/** Content block stop */
export interface ContentBlockStopEvent extends BaseStreamEvent {
	type: EventType.ContentBlockStop;
	id?: string;
	[key: string]: unknown;
}

/** Tool use */
export interface ToolUseEvent extends BaseStreamEvent {
	type: EventType.ToolUse;
	id: string;
	name: string;
	input?: Record<string, unknown>;
	input_text?: string;
	[key: string]: unknown;
}

/** Tool result */
export interface ToolResultEvent extends BaseStreamEvent {
	type: EventType.ToolResult;
	tool_use_id: string;
	content?: string | unknown;
	result?: Record<string, unknown>;
	[key: string]: unknown;
}

/** Thinking */
export interface ThinkingEvent extends BaseStreamEvent {
	type: EventType.Thinking;
	content?: string;
	text?: string;
	[key: string]: unknown;
}

/** Reasoning */
export interface ReasoningEvent extends BaseStreamEvent {
	type: EventType.Reasoning;
	content?: string;
	text?: string;
	[key: string]: unknown;
}

/** Plan delta */
export interface PlanDeltaEvent extends BaseStreamEvent {
	type: EventType.PlanDelta;
	text: string;
	[key: string]: unknown;
}

/** Keep-alive ping */
export interface KeepAliveEvent extends BaseStreamEvent {
	type: EventType.KeepAlive;
	message?: string;
}

/** Execution metadata */
export interface MetadataEvent extends BaseStreamEvent {
	type: EventType.Metadata;
	run_id?: number;
	mode?: "task" | "chat";
	run_uuid?: string;
	sandbox_id?: string;
	sandbox_enabled?: boolean;
	agent_name?: string;
}

/** Execution complete */
export interface DoneEvent extends BaseStreamEvent {
	type: EventType.Done;
	message_count?: number;
}

/** Error event */
export interface ErrorEvent extends BaseStreamEvent {
	type: EventType.Error;
	error: string;
	message: string;
}

/** Message snapshot (persisted message) */
export interface MessageSnapshotEvent extends BaseStreamEvent {
	type: EventType.MessageSnapshot;
	role: string;
	content: string;
	tool_calls: Array<{
		id: string;
		tool_name: string;
		input?: Record<string, unknown>;
		result?: string;
	}>;
	thinking?: string;
	plan?: string;
	usage?: {
		input_tokens: number;
		output_tokens: number;
	};
	message_id?: string;
	event_type: string;
	event_count: number;
	trace: unknown[];
}

/** Sandbox connecting */
export interface SandboxConnectingEvent extends BaseStreamEvent {
	type: EventType.SandboxConnecting;
	message: string;
}

/** Sandbox connected */
export interface SandboxConnectedEvent extends BaseStreamEvent {
	type: EventType.SandboxConnected;
	sandbox_id: string;
	duration_ms?: number;
	message: string;
}

/** MCP authentication failed */
export interface McpAuthFailedEvent extends BaseStreamEvent {
	type: EventType.McpAuthFailed;
	error: string;
	source?: string;
}

/** MCP error */
export interface McpErrorEvent extends BaseStreamEvent {
	type: EventType.McpError;
	error: string;
	source?: string;
}

/** Cancelled event */
export interface CancelledEvent extends BaseStreamEvent {
	type: EventType.Cancelled;
	run_id?: number;
}

/** Run metrics */
export interface RunMetricsEvent extends BaseStreamEvent {
	type: EventType.RunMetrics;
	model: string;
	execution_time_ms: number;
	input_tokens_used?: number;
	output_tokens_used?: number;
	claude_token_cost_usd?: number;
}

/** Sandbox metrics */
export interface SandboxMetricsEvent extends BaseStreamEvent {
	type: EventType.SandboxMetrics;
	sandbox_id: string;
	connection_duration_ms?: number;
	execution_duration_ms?: number;
}

/** SDK initialization */
export interface SdkInitEvent extends BaseStreamEvent {
	type: EventType.SdkInit;
	[key: string]: unknown;
}

/** SDK success */
export interface SdkSuccessEvent extends BaseStreamEvent {
	type: EventType.SdkSuccess;
	[key: string]: unknown;
}

/** SDK error */
export interface SdkErrorEvent extends BaseStreamEvent {
	type: EventType.SdkError;
	error?: string;
	[key: string]: unknown;
}

/** SDK content snapshot */
export interface SdkContentSnapshotEvent extends BaseStreamEvent {
	type: EventType.SdkContentSnapshot;
	[key: string]: unknown;
}

/** Stderr output */
export interface StderrEvent extends BaseStreamEvent {
	type: EventType.Stderr;
	message?: string;
	[key: string]: unknown;
}

/** User message from subagent */
export interface UserMessageEvent extends BaseStreamEvent {
	type: EventType.UserMessage;
	content?: string;
	uuid?: string;
	parent_tool_use_id?: string;
	[key: string]: unknown;
}

/** Compact boundary (conversation summarization marker) */
export interface CompactBoundaryEvent extends BaseStreamEvent {
	type: EventType.CompactBoundary;
	trigger?: "manual" | "auto";
	pre_tokens?: number;
	[key: string]: unknown;
}

/** System message (generic, use subtype for specific handling) */
export interface SystemMessageEvent extends BaseStreamEvent {
	type: EventType.SystemMessage;
	subtype?: string;
	data?: Record<string, unknown>;
	[key: string]: unknown;
}

/** Runner diagnostics (debug info from agent_runner.py startup) */
export interface RunnerDiagEvent extends BaseStreamEvent {
	type: EventType.RunnerDiag;
	[key: string]: unknown;
}

/** Permission request (tool approval needed) */
export interface PermissionRequestEvent extends BaseStreamEvent {
	type: EventType.PermissionRequest;
	request_id: string;
	tool_name: string;
	tool_input: Record<string, unknown>;
	timeout_seconds: number;
}

/** Awaiting approval (run paused for permission) */
export interface AwaitingApprovalEvent extends BaseStreamEvent {
	type: EventType.AwaitingApproval;
}

/** Union type of all streaming events (SDK + FastAPI) */
export type AgentStreamEvent =
	// SDK Message Events
	| MessageStartEvent
	| MessageDeltaEvent
	| MessageStopEvent
	| MessageCompleteEvent
	// SDK Content Events
	| ContentBlockStartEvent
	| ContentBlockDeltaEvent
	| ContentBlockStopEvent
	// SDK Tool Events
	| ToolUseEvent
	| ToolResultEvent
	// SDK Extended Content
	| ThinkingEvent
	| ReasoningEvent
	| PlanDeltaEvent
	// SDK Internal Events
	| SdkInitEvent
	| SdkSuccessEvent
	| SdkErrorEvent
	| SdkContentSnapshotEvent
	| StderrEvent
	| UserMessageEvent
	// System Message Events
	| CompactBoundaryEvent
	| SystemMessageEvent
	// FastAPI Custom Events
	| KeepAliveEvent
	| MetadataEvent
	| DoneEvent
	| ErrorEvent
	| MessageSnapshotEvent
	| SandboxConnectingEvent
	| SandboxConnectedEvent
	| McpAuthFailedEvent
	| McpErrorEvent
	| CancelledEvent
	| PermissionRequestEvent
	| AwaitingApprovalEvent
	| RunMetricsEvent
	| SandboxMetricsEvent
	// Runner Diagnostics
	| RunnerDiagEvent;
